<template>
    <div class="dashboard-graphs-container">
        <div class="w-100">
            <div class="row mx-0 my-3">
                <!-- Fuel type -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'mb-2 order-1' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.mdAndDown}"
                >
                    <div class="position-relative">
                        <multiselect v-model="filters.fuels" :options="fuelsList" :multiple="true" :close-on-select="false" :clear-on-select="false" :preserve-search="true" 
                            placeholder="Search fuel here..." track-by="_id" selectedLabel="" selectLabel="" deselectLabel="" label="name">
                            <template slot="selection" slot-scope="{ values, search, isOpen }">
                                <span class="multiselect__single" v-if="values.length && !isOpen">
                                    <span v-if="values.length > 1">{{ values.length }} fuel types selected</span>
                                    <span v-else>{{ values[0].name }}</span>
                                </span>
                            </template>
                            <template slot="noResult">
                                No matching fuel type
                            </template>
                        </multiselect>
                    </div>
                </div>

                <!-- Terminals list -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'mb-2 order-1' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.smAndDown}"
                >
                    <div class="position-relative">
                        <multiselect v-model="filters.terminals" :options="getTerminalChanges" :multiple="true" :close-on-select="false" :clear-on-select="false" :preserve-search="true" 
                            placeholder="Search terminal here..." track-by="_id" selectedLabel="" selectLabel="" deselectLabel="" label="name"
                            :disabled="filters.fuels.find(fuel => fuel.type == 'BUNKERING') && !filters.fuels.find(fuel => fuel.type == 'SPOT')">
                            <template slot="selection" slot-scope="{ values, search, isOpen }">
                                <span class="multiselect__single" v-if="values.length && !isOpen">
                                    <span v-if="values.length > 1">{{ values.length }} terminals selected</span>
                                    <span v-else>{{ values[0].name }}</span>
                                </span>
                            </template>
                            <template slot="noResult">
                                No matching terminal
                            </template>
                        </multiselect>
                    </div>
                </div>

                <!-- Counterpart list -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'mb-2 order-1 ' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.mdAndDown}"
                >
                    <div class="position-relative">
                        <multiselect v-model="filters.counterparts" :options="counterpartList" :multiple="true" :close-on-select="false" :clear-on-select="false" :preserve-search="true" 
                            :placeholder="'Search ' + (userLoggedIn.category == 'buyer' ? 'sellers' : 'buyers') + ' here...'" 
                            track-by="id" selectedLabel="" selectLabel="" deselectLabel="" label="name">
                            <template slot="selection" slot-scope="{ values, search, isOpen }">
                                <span class="multiselect__single" v-if="values.length && !isOpen">
                                    <span v-if="values.length > 1">{{ values.length }} {{ userLoggedIn.category == 'buyer' ? 'sellers' : 'buyers' }} selected</span>
                                    <span v-else>{{ values[0].name }}</span>
                                </span>
                            </template>
                            <template slot="noResult">
                                No matching {{ userLoggedIn.category == 'buyer' ? 'seller' : 'buyer' }}
                            </template>
                        </multiselect>
                    </div>
                </div>

                <!-- Date range selector -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'order-0 mb-2' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.smAndDown,  'pr-3': !$breakpoint.mdAndDown}"
                >
                    <vc-date-picker 
                        mode="range" 
                        v-model="filters.dateRange" 
                        :masks="{ input: 'DD/MMM/YY' }" 
                        class="vc-date-picker"
                        :input-props='{ placeholder: filters.dateRange , readonly: true }'
                    >
                    </vc-date-picker>
                </div>
            </div>

            <apexchart type="bar" width="100%" height="340px" v-if="fuelsList.length > 0"
                :options="chartOptions" :series="series" ref="fuelChart">
            </apexchart>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import breakpoint from '@/mixins/index';
import { getOrderStatistics } from '@/api/orders';
const numeral = require("numeral");
const moment = require('moment');

export default {
    mixins: [breakpoint],
    data(){
        return {
            filters: {
                fuels: [],
                terminals: [],
                counterparts: [],
                dateRange: {
                    start: new Date(moment().subtract(30, 'days')),
                    end: new Date()
                },
            },
            orderStatistics: {},
        }
    },
    created: async function() {
        if (this.userLoggedIn.category == 'seller')
            await this.getRelationships();

        await this.setOrderStatistics();
    },
    computed: {
        ...mapState('accounts', ['suppliers', 'customerList']),
        ...mapGetters('fuelsType', ['getFuelsType']),
        ...mapState('login', ['userLoggedIn']),
        ...mapGetters('terminals', ['getTerminalChanges']),
        ...mapGetters('metadata', ['productConfig', 'networkConfig']),
        currentFuels: {
          get: function() {
            return this.filters.fuels.length > 0 ? this.filters.fuels : this.fuelsList
          }
        },
        fuelsList: {
            get: function(){
                let fuels = [];

                if (this.getFuelsType.length > 0){
                    fuels = this.getFuelsType.filter(fuel => fuel.type !== 'NOT4SALE');
                }

                return fuels;
            }
        },
        counterpartList: {
            get: function(){
                let counterparts = []

                if ((this.userLoggedIn.category == 'buyer' && this.suppliers.length > 0) ||
                    (this.userLoggedIn.category == 'seller' && this.customerList.length > 0)){
                    counterparts = this.userLoggedIn.category == 'buyer' ? this.suppliers : this.customerList
                }

                return counterparts;
            }
        },
        series: {
            get: function(){
              return this.getSeriesData(this.orderStatistics);
            }
        },
        chartOptions: function(){
            if (this.fuelsList.length > 0){
                return {
                    plotOptions: {
                        bar: {
                            distributed: true
                        }
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    fill: {
                        colors: ['#63B3ED', '#4299E1', '#1476BD', '#2B6CB0', '#2C5282']
                    },
                    noData: {
                        text: "No Data for that criteria",
                        align: "center",
                        verticalAlign: "middle"
                    },
                    chart: {
                        id: 'vuechart-example',
                    },
                    xaxis: {
                        categories: (this.currentFuels).map(fuel => fuel.name)
                    },
                    yaxis: {
                        labels: {
                            formatter: function(value) {
                                return numeral(value).format("0,0");
                            }
                        }
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 2000,
                        animateGradually: {
                            enabled: true,
                            delay: 100,
                        },
                        dynamicAnimation: {
                            enabled: true,
                            speed: 100,
                        }
                    }
                }
            }
        },
        removeTerminalsOnBunkering(){
            if (this.filters.fuels){
                if (this.filters.fuels.find(fuel => fuel.type == 'BUNKERING') && !this.filters.fuels.find(fuel => fuel.type == 'SPOT'))
                    return this.filters.terminals = []
            }
        }
    },
    watch: {
        chartOptions(){},
        removeTerminalsOnBunkering(){},
        "filters.fuels"(){
          this.setOrderStatistics()
        },
        "filters.dateRange.start"(){
          this.setOrderStatistics()
        },
        "filters.dateRange.end"(){
          this.setOrderStatistics()
        },
        "filters.terminals"(){
          this.setOrderStatistics()
        },
        "filters.counterparts"(){
          this.setOrderStatistics()
        },
    },
    methods: {
        ...mapActions('accounts', ['getRelationships']),
        async setOrderStatistics(){
          const payload = {
            fuels: this.currentFuels.map(({_id}) => _id),
            startDate: `${moment(this.filters.dateRange.start).format("YYYY-MM-DD")}T01:00:00.000000Z`,
            endDate: `${moment(this.filters.dateRange.end).format("YYYY-MM-DD")}T23:00:00.000000Z`,
            terminals: this.filters.terminals.map(({_id}) => _id),
            counterparts: this.filters.counterparts.map(({id}) => id),
          }
          const { statistics } = await getOrderStatistics(payload);
          this.orderStatistics = statistics;
        },
        getSeriesData(statistics){
          const data = this.currentFuels.map(({_id}) => statistics && statistics[_id] ? statistics[_id] :  0 );
          return [{ name: "Total volume", data}];
        },
    }
}
</script>

<style lang="scss">
@import "@/assets/scss/components/_datepicker.scss";
</style>
